import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarRange, Check, Loader2, Plus, Trash2 } from 'lucide-react';
import { createSaison, deleteSaison, fetchSaisons, updateSaison } from '../services/api';

/**
 * Les saisons du club.
 *
 * Une saison sportive ne suit pas l'année civile : elle court de l'été à
 * l'été, et c'est elle qui découpe le registre. Une seule est courante — c'est
 * celle qu'on propose à l'inscription et sur laquelle porte le tableau du
 * trésorier.
 *
 * Rien ne s'efface une fois qu'une saison porte des licences : ce serait
 * effacer l'année entière du club.
 */
export default function SaisonsClub() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['saisons'], queryFn: fetchSaisons });

  const [form, setForm] = useState({ libelle: '', debut: '', fin: '', courante: true });
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const annoncer = (type: 'success' | 'error', message: string) => {
    setNotice({ type, message });
    if (type === 'success') setTimeout(() => setNotice(null), 3000);
  };

  const rafraichir = () => {
    queryClient.invalidateQueries({ queryKey: ['saisons'] });
    queryClient.invalidateQueries({ queryKey: ['licences'] });
  };

  const ouvrir = useMutation({
    mutationFn: () => createSaison(form),
    onSuccess: (d: any) => {
      annoncer('success', d.message);
      setForm({ libelle: '', debut: '', fin: '', courante: true });
      rafraichir();
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  const rendreCourante = useMutation({
    mutationFn: (id: number) => updateSaison(id, { courante: true }),
    onSuccess: (d: any) => {
      annoncer('success', d.message);
      rafraichir();
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  const fermer = useMutation({
    mutationFn: (id: number) => deleteSaison(id),
    onSuccess: (d: any) => {
      annoncer('success', d.message);
      rafraichir();
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  if (query.isPending) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const saisons: any[] = query.data?.saisons ?? [];

  // Une proposition qui tombe juste neuf fois sur dix : la saison qui vient.
  const proposer = () => {
    const annee = new Date().getFullYear();
    setForm({
      libelle: `${annee}-${annee + 1}`,
      debut: `${annee}-09-01`,
      fin: `${annee + 1}-08-31`,
      courante: true,
    });
  };

  return (
    <div className="space-y-4">
      {notice && (
        <div
          className={`rounded-lg px-4 py-2 text-sm ${
            notice.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {notice.message}
        </div>
      )}

      {saisons.length === 0 ? (
        <p className="text-sm text-gray-500">
          Aucune saison n'est ouverte. Tant qu'il n'y en a pas, personne ne peut être inscrit.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {saisons.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-3 py-2.5">
              <CalendarRange className="w-4 h-4 text-gray-400 shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-gray-900">
                  {s.libelle}
                  {s.courante && (
                    <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-xs font-medium">
                      <Check className="w-3 h-3" />
                      courante
                    </span>
                  )}
                </span>
                <span className="block text-xs text-gray-500">
                  du {s.debut} au {s.fin}
                </span>
              </span>
              {!s.courante && (
                <button
                  onClick={() => rendreCourante.mutate(s.id)}
                  className="text-sm text-primary-700 hover:text-primary-800"
                >
                  Rendre courante
                </button>
              )}
              <button
                onClick={() => {
                  if (window.confirm(`Fermer la saison ${s.libelle} ?`)) fermer.mutate(s.id);
                }}
                title="Fermer la saison"
                className="p-1.5 rounded text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="border-t border-gray-100 pt-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={form.libelle}
            onChange={(e) => setForm({ ...form, libelle: e.target.value })}
            placeholder="2026-2027"
            className="w-32 px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
          />
          <input
            type="date"
            value={form.debut}
            onChange={(e) => setForm({ ...form, debut: e.target.value })}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
          />
          <span className="text-xs text-gray-400">au</span>
          <input
            type="date"
            value={form.fin}
            onChange={(e) => setForm({ ...form, fin: e.target.value })}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
          />
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={form.courante}
              onChange={(e) => setForm({ ...form, courante: e.target.checked })}
              className="rounded border-gray-300 text-primary-600"
            />
            Courante
          </label>
          <button
            onClick={() => ouvrir.mutate()}
            disabled={ouvrir.isPending || !form.libelle.trim() || !form.debut || !form.fin}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
          >
            {ouvrir.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Ouvrir
          </button>
        </div>
        <button onClick={proposer} className="text-xs text-primary-700 hover:text-primary-800">
          Proposer la saison qui vient
        </button>
      </div>
    </div>
  );
}
